import React, { useState } from 'react';
import { Box, List } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import THEME from '../../constants/theme';
import { useNavigation } from '../../context/NavigationContext';
import {
  useIntersectionObserver,
  getLogo,
  getNavigationMenuItems,
  navigateToPathOrSection
} from '../../utils/navigationUtils';

import NavigationMenuItem from './NavigationMenuItem';
import PoweredBySpinnaker from './PoweredBySpinnaker';
import ServiceStatusModal from '../common/ServiceStatusModal';

const NavigationMenu = ({
  pageSectionRefsContainer,
  textColor = '#000000',
  width = 250,
  isHomePage = false
}) => {
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const {
    expandedMenu,
    handleMenuToggle,
    activeSection,
    setActiveSection,
    startPageTransition
  } = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = getNavigationMenuItems();
  const activeColor = THEME.COLORS.PRIMARY_PURPLE;

  useIntersectionObserver(
    pageSectionRefsContainer ? pageSectionRefsContainer.current : {},
    setActiveSection
  );

  const handleSectionScroll = (sectionId, parentPath) => {
    const currentRefs = pageSectionRefsContainer
      ? pageSectionRefsContainer.current
      : {};
    navigateToPathOrSection(
      sectionId,
      parentPath,
      currentRefs,
      navigate,
      location.pathname,
      startPageTransition
    );
  };

  const handleServiceStatusClick = () => {
    setServiceModalOpen(true);
  };

  const handleServiceModalClose = () => {
    setServiceModalOpen(false);
  };

  const logoSrc = getLogo(textColor);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        height: '100vh',
        width: width,
        background: isHomePage 
          ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.05) 100%)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        color: textColor,
        display: {
          xs: 'none',
          sm: 'none',
          md: 'block'
        },
        borderLeft: isHomePage ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
        paddingTop: isHomePage ? '8vh' : '3vh',
        paddingRight: '2vh',
        paddingLeft: '1vh',
        zIndex: 10,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxShadow: isHomePage ? 'none' : '0 2px 20px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Box
        sx={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <List sx={{ 
          padding: '20px 0',
          '& .MuiListItem-root': {
            borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
            margin: '4px 0',
            transition: 'all 0.3s ease',
            '&:hover': {
                    backgroundColor: isHomePage 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(169, 64, 255, 0.08)',
              transform: 'translateX(-4px)'
            }
          }
        }}>
          {menuItems.map(item => (
            <React.Fragment key={item.label}>
              <NavigationMenuItem
                item={item}
                expandedMenu={expandedMenu}
                handleMenuToggle={handleMenuToggle}
                handleScroll={handleSectionScroll}
                location={location}
                activeSection={activeSection}
                textColor={textColor}
                activeColor={activeColor}
                startPageTransition={startPageTransition}
                navigate={navigate}
                onServiceStatusClick={handleServiceStatusClick}
              />
            </React.Fragment>
          ))}
        </List>

        {/* Logo Section */}
        <Box
          component='a'
          href='https://www.spinnakeralgo.com/'
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '15px 5px',
            marginTop: '20px',
            cursor: 'pointer',
            textDecoration: 'none',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
          aria-label='Visit Spinnaker website'
        >
          <img
            src={logoSrc}
            alt='Sentinel Logo'
            style={{
              width: '100%',
              maxWidth: `${width * 0.95}px`,
              height: 'auto',
              filter: textColor === '#ffffff' ? 'none' : 'none'
            }}
          />
        </Box>

        <Box sx={{ marginTop: 'auto', paddingBottom: '112px' }}>
          <Box
            sx={{
              borderTop: isHomePage
                ? '1px solid rgba(255, 255, 255, 0.14)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              paddingTop: '10px'
            }}
          >
            <PoweredBySpinnaker isHomePage={isHomePage} centered />
          </Box>
        </Box>
      </Box>

      {/* Service Status Modal */}
      <ServiceStatusModal
        open={serviceModalOpen}
        onClose={handleServiceModalClose}
      />
    </Box>
  );
};

NavigationMenu.propTypes = {
  pageSectionRefsContainer: PropTypes.object,
  textColor: PropTypes.string,
  width: PropTypes.number
};

export default NavigationMenu;
